import React, { useState, useEffect } from 'react';
import { Typography, Spin, Card, Divider, List, Table, Space, Badge, Switch, Collapse, Avatar } from 'antd';
import { EnvironmentOutlined,  GlobalOutlined } from '@ant-design/icons';
import '../styles/earthdaily-theme.css';
import '../styles/sidebar.css';
import { toPascalCase } from '../utils/textFormatter';
import { SquareSeedIcon } from './SeedIcons';

import { cropColorMap } from '../utils/colorMap';
import { CountryBasicInfo, Legend as LegendType } from '../types';
import NavigationBreadcrumb from './NavigationBreadcrumb';
import Meta from 'antd/es/card/Meta';
const { Text, Paragraph } = Typography;

interface FeatureTypeStats {
  type: string;
  count: number;
  totalArea: number;
}

interface FeatureStats {
  name: string;
  description?: string;
  typeAreaStats: FeatureTypeStats[];
}

interface SidebarProps {
  countries: CountryBasicInfo[];
  activeCountry: string | null;
  loading: boolean;
  geoJSONData?: any | null;
  legendData?: LegendType | null;
  activePOI?: string | null;
  activeSeason?: string | null;
  visibleLevel?: 'countries' | 'pois' | 'features';
  hoveredCropType?: string | null;
  onCountryClick?: (countryName: string) => void;
  onPOIClick?: (poiName: string) => void;
  onSeasonClick?: (season: number) => void;
  onCropFilterChange?: (cropTypes: string[]) => void;
  onCropHover?: (cropType: string | null) => void;
  onNavigate?: (level: 'countries' | 'pois' | 'features') => void;
}

const getCropIcon = (cropType: string, legendData?: any) => {
  let color = cropColorMap[cropType.toLowerCase()] || '#FFFFFF';
  if (legendData?.items) {
    const legendItem = legendData.items.find((item: any) => item.value.toString() === cropType);
    if (legendItem) color = legendItem.color;
  }
  return <SquareSeedIcon style={{ color }} />;
};

const getCropLabel = (cropType: string, legendData?: any) => {
  if (legendData?.items) {
    const legendItem = legendData.items.find((item: any) => item.value.toString() === cropType);
    if (legendItem) return legendItem.label;
  }
  const labels: Record<string, string> = {
    'winter.wheat': 'Winter Wheat',
    'winter_wheat': 'Winter Wheat',
    '2nd_corn': '2nd Corn',
    'other_veg': 'Other Vegetation',
    'other_ag': 'Other Agricultural'
  };
  return labels[cropType.toLowerCase()] || toPascalCase(cropType);
};

const Sidebar: React.FC<SidebarProps> = ({
  countries,
  activeCountry,
  loading,
  geoJSONData,
  legendData,
  activePOI,
  activeSeason,
  visibleLevel = 'countries',
  hoveredCropType,
  onCountryClick,
  onPOIClick,
  onSeasonClick,
  onCropFilterChange,
  onCropHover,
  onNavigate
}) => {
  const [poiStats, setPoiStats] = useState<Record<string, FeatureStats>>({});
  const [useKm2, setUseKm2] = useState(true);
  const [expandedYear, setExpandedYear] = useState<string | null>(null);
  const [selectedCropFilters, setSelectedCropFilters] = useState<string[]>([]);
  const activeCountryData = countries.find(c => c.name === activeCountry);


  useEffect(() => {
    if (!geoJSONData?.features) {
      setPoiStats({});
      return;
    }

    const markers = geoJSONData.features.filter(
      (feature: any) => feature.properties?.type === 'marker'
    );

    const stats: Record<string, FeatureStats> = {};

    markers.forEach((marker: any) => {
      const markerName = marker.properties?.name || 'Unknown';
      const seasons = marker.properties?.seasons || [];
      const currentSeason = seasons.find((s: any) => s.id === activeSeason) || seasons[0];
      
      if (!currentSeason) return;

      // Utiliser les stats du JSON si disponibles
      if (currentSeason.stats) {
        stats[markerName] = {
          name: markerName,
          description: marker.properties?.description || '',
          typeAreaStats: currentSeason.stats
            .map((stat: any) => ({
              type: stat.type,
              count: stat.count,
              totalArea: stat.totalArea
            }))
            .sort((a: any, b: any) => b.totalArea - a.totalArea)
        };
      }
    });

    setPoiStats(stats);
  }, [geoJSONData, activeSeason, activePOI]);

  const activeStats = activePOI ? poiStats[activePOI] : null;

  // Réinitialiser la saison et l'année quand le POI change
  useEffect(() => {
    setExpandedYear(null);
  }, [activePOI]);

  // Réinitialiser la saison et l'année quand le pays change
  useEffect(() => {
    setExpandedYear(null);
  }, [activeCountry]);

  // Colonnes pour le tableau des statistiques d'aire
  const areaStatsColumns = [
    {
      title: 'Crop',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => (
        <Space>
          {getCropIcon(text, legendData)}
          <Text strong>{getCropLabel(text, legendData)}</Text>
        </Space>
      )
    },
    {
      title: `Total Area (${useKm2 ? 'ha' : 'acres'})`,
      dataIndex: 'totalArea',
      key: 'totalArea',
      render: (value: number) => {
        const displayValue = useKm2 ? value * 100 : value * 247.105; // km² to ha: *100, km² to acres: *247.105
        return (
          <span>
            {displayValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        );
      }
    }
  ];

  const handleCropClick = (cropType: string) => {
    const newFilters = selectedCropFilters.includes(cropType)
      ? selectedCropFilters.filter(c => c !== cropType)
      : [...selectedCropFilters, cropType];
    setSelectedCropFilters(newFilters);
    onCropFilterChange?.(newFilters);
  };

  const clearAllFilters = () => {
    setSelectedCropFilters([]);
    onCropFilterChange?.([]);
  };

  const renderGlobalView = () => (
    <Card title={
      <Space>
        <GlobalOutlined />
        <span>Explore Available Countries</span>
      </Space>
    } className="countries-card">
      <Paragraph>
        Click on any country marker on the map to explore its agricultural data.
      </Paragraph>
      <List
        size="small"
        dataSource={countries}
        renderItem={country => (
          <List.Item
            style={{ cursor: 'pointer' }}
            onClick={() => onCountryClick?.(country.name)}
          >
            <Space>
              <Badge color={country.color} />
              <Text>{country.name}</Text>
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );

  const renderCountryStats = () => {
    if (!activeCountry) return renderGlobalView();

    return (
      <Card title={
        <Space>
          <Badge color={activeCountryData?.color || '#333'} />
          <span>{activeCountry}</span>
        </Space>
      } className="country-card">
        <List
          size="small"
          dataSource={Object.values(poiStats)}
          renderItem={stat => {
            const marker = geoJSONData?.features?.find(
              (f: any) => f.properties?.type === 'marker' && f.properties?.name === stat.name
            );
            const seasons = marker?.properties?.seasons || [];
            const yearGroups = seasons.reduce((acc: any, season: any) => {
              if (!acc[season.year]) acc[season.year] = [];
              acc[season.year].push(season);
              return acc;
            }, {});

            return (
              <List.Item
                style={{ cursor: 'pointer' }}
                onClick={() => onPOIClick?.(stat.name)}
              >
                <List.Item.Meta
                  title={stat.name}
                  description={
                    seasons.length > 0 ? (
                      <div style={{ marginTop: '4px' }}>
                        {Object.entries(yearGroups).map(([year]: [string, any]) => (
                          <div key={year} style={{ fontSize: '12px', marginBottom: '2px' }}>
                            <Space>
                              <Badge status='default' />
                              <Text strong>{year}</Text>
                            </Space>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Text type="secondary">No seasons available</Text>
                    )
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>
    );
  };

  const renderPOIStats = () => {
    if (!activeStats || !geoJSONData) return null;

    const marker = geoJSONData.features?.find(
      (f: any) => f.properties?.type === 'marker' && f.properties?.name === activeStats.name
    );
    const seasons = marker?.properties?.seasons || [];
    
    // Grouper les saisons par année
    const yearGroups = seasons.reduce((acc: any, season: any) => {
      if (!acc[season.year]) acc[season.year] = [];
      acc[season.year].push(season);
      return acc;
    }, {});
    
    const years = Object.keys(yearGroups);
    
    // Sélectionner automatiquement la première année si aucune n'est sélectionnée
    const currentExpandedYear = expandedYear || (years.length > 0 ? years[0] : null);
    
    // Auto-sélectionner la première saison si nécessaire
    if (currentExpandedYear && !activeSeason && yearGroups[currentExpandedYear]?.[0]) {
      setTimeout(() => {
        onSeasonClick?.(yearGroups[currentExpandedYear][0].id);
        setExpandedYear(currentExpandedYear);
      }, 0);
    }
    
    const collapseItems = years.map(year => {
      const yearSeason = yearGroups[year]?.[0];
      const seasonStats = yearSeason?.stats || [];
      
      return {
        key: year,
        label: <Space><Text strong>{year}</Text></Space>,
        children: seasonStats.length > 0 && (
          <>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Crop area statistics {selectedCropFilters.length > 0 && `(${selectedCropFilters.length} filtered)`}
              </Text>
              <Space size="small">
                <Text type="secondary" style={{ fontSize: '11px' }}>ha</Text>
                <Switch
                  checked={!useKm2}
                  onChange={(checked) => setUseKm2(!checked)}
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: '11px' }}>acres</Text>
              </Space>
            </div>
            <Table
              dataSource={seasonStats.map((stat: any) => ({
                type: stat.type,
                count: stat.count,
                totalArea: stat.totalArea
              })).sort((a: any, b: any) => {
                const aLabel = getCropLabel(a.type, legendData);
                const bLabel = getCropLabel(b.type, legendData);
                const isASpecial = aLabel === 'Other Ag' || aLabel === 'Non Cultivated';
                const isBSpecial = bLabel === 'Other Ag' || bLabel === 'Non Cultivated';
                
                if (isASpecial && !isBSpecial) return 1;
                if (!isASpecial && isBSpecial) return -1;
                if (isASpecial && isBSpecial) return 0;
                
                return b.totalArea - a.totalArea;
              })}
              columns={areaStatsColumns}
              pagination={false}
              size="small"
              rowKey="type"
              className="area-stats-table"
              showHeader={false}
             
            />
            {selectedCropFilters.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Space size="small" wrap>
                  {selectedCropFilters.map(crop => (
                    <Text 
                      key={crop}
                      style={{ 
                        fontSize: '10px', 
                        cursor: 'pointer', 
                        color: '#15b89a',
                        backgroundColor: '#f0f9ff',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        border: '1px solid #15b89a'
                      }}
                      onClick={() => handleCropClick(crop)}
                    >
                      {getCropLabel(crop, legendData)} ×
                    </Text>
                  ))}
                  <Text 
                    type="secondary" 
                    style={{ fontSize: '10px', cursor: 'pointer', color: '#ff4d4f' }}
                    onClick={clearAllFilters}
                  >
                    Clear all
                  </Text>
                </Space>
              </div>
            )}
          </>
        )
      };
    });

    const handleCollapseChange = (key: string | string[]) => {
      const yearKey = Array.isArray(key) ? key[0] : key;
      setExpandedYear(yearKey || null);
      if (yearKey && yearGroups[yearKey]?.[0]) {
        onSeasonClick?.(yearGroups[yearKey][0].id);
      }
    };

    return (
      <Card
        title={
          <Space>
            <EnvironmentOutlined />
            <span>{activeStats.name}</span>
          </Space>
        }
        className="poi-card"
      >

        {years.length > 0 && (
          <>
            <Divider>Available Years</Divider>
            <Collapse 
              accordion
              activeKey={currentExpandedYear || undefined}
              onChange={handleCollapseChange}
              ghost
              size="small"
              items={collapseItems}
            />
          </>
        )}

    
      </Card>
    );
  };

  const renderMainContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '10px' }}>Loading data...</div>
        </div>
      );
    }

    switch (visibleLevel) {
      case 'countries':
        return renderGlobalView();
      case 'pois':
        return renderCountryStats();
      case 'features':
        return renderPOIStats();
      default:
        return renderGlobalView();
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* En-tête fixe - masqué sur mobile car déplacé dans App.tsx */}
      {window.innerWidth > 768 && (
        <div style={{
          padding: '15px',
          borderTop: '1px solid rgba(0,0,0,0.05)',
          backgroundColor: '#f9f9f9',
          textAlign: 'center'
        }}>
          <a href='/' style={{ textDecoration: 'none' }}>
          <div>
            <img
              src="/assets/LoB_logo_Agriculture.png"
              alt="EarthDaily Logo"
              style={{ height: '50px', display: 'block', margin: '0 auto' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text style={{ color: '#214b64', opacity: 0.8 }}>
                Explore geospatial agricultural data
              </Text>
            </div>
          </div>
          </a>
        </div>
      )}

        {/* Breadcrumb Navigation */}
      {onNavigate && (
        <NavigationBreadcrumb
          visibleLevel={visibleLevel}
          activeCountry={activeCountry}
          activePOI={activePOI || null}
          onNavigate={onNavigate}
        />
      )}


      {/* Contenu principal avec défilement */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        paddingTop: '0'
      }}>
        {renderMainContent()}
      </div>

      {/* Pied de page fixe */}
      <div style={{
        padding: '15px',
        borderTop: '1px solid rgba(0,0,0,0.05)',
        backgroundColor: '#f9f9f9',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '12px',
          color: 'var(--text-light)',
          marginTop: '8px'
        }}>
          © 2025 EarthDaily Analytics
        </div>
      </div>
    </div>
  );
};

export default Sidebar;